import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { PlatformView, PlatformViews } from '@img-arena/consoles-api-streaming'
import { useTheme } from '@mui/material'
import { PieChart } from '@/components/charts/PieChart'
import { HorizontalTable } from '@/components/horizontalTable/HorizontalTable'
import { HorizontalTableSkeleton } from '@/components/horizontalTable/HorizontalTableSkeleton'
import { streamingRestApi } from '@/service/streamingApi'

import { ChartDataItem } from '../../../charts/types'
import { StatisticsCard, StatisticsCardSkeleton } from '../../StatisticsCard'
import { STATISTICS_CSV_HEADERS } from '../constants'
import { ComponentWithStatisticsQueryParams, StatisticsCardTableData } from '../types'
import { PLATFORM_POPULARITY_ROWS } from './platformPopularity.table'

export const PLATFORM_POPULARITY_CARD_TEST_ID = 'platform-popularity-card' as const
export const PLATFORM_POPULARITY_SKELETON_TEST_ID = 'platform-popularity-skeleton' as const

const CSV_HEADERS = [
  STATISTICS_CSV_HEADERS.USER_AGENT,
  STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
  STATISTICS_CSV_HEADERS.TOTAL_BYTES,
  STATISTICS_CSV_HEADERS.TOTAL_DURATON,
  STATISTICS_CSV_HEADERS.AVERAGE_DURATION,
  STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
  STATISTICS_CSV_HEADERS.UNIQUE_VIEWS
]

export type PlatformPopularityTableData = StatisticsCardTableData<PlatformView>

export const PlatformPopularity: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics
  const theme = useTheme()

  const [platformPopularityData, setPlatformPopularityData] = useState<
    PlatformViews['views'] | null
  >(null)
  const [highlightedItem, setHighlightedItem] = useState<ChartDataItem | null>(null)

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getPlatformViews(queryParams, ac.signal).then((data) => {
      setPlatformPopularityData(data.views)
    })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  const getPlatformColor = useCallback(
    (platform?: string) => {
      if (!platform) return theme.palette.platformColors.OTHER

      const platformName = platform.toUpperCase() as keyof typeof theme.palette.platformColors

      return theme.palette.platformColors[platformName] || theme.palette.platformColors.OTHER
    },
    [theme]
  )

  const tableData: PlatformPopularityTableData[] = useMemo(() => {
    if (!platformPopularityData) return []

    return platformPopularityData.map((item) => {
      const { color, background } = getPlatformColor(item.userAgent)

      const {
        userAgent,
        averageDuration,
        percentageViews,
        totalBytes,
        totalDuration,
        totalViews,
        uniqueViews
      } = item

      return {
        userAgent,
        averageDuration,
        percentageViews,
        totalBytes,
        totalViews,
        totalDuration,
        uniqueViews,
        label: userAgent,
        color: background,
        textColor: color
      }
    })
  }, [platformPopularityData])

  const chartData = useMemo(() => {
    if (!tableData) return []

    return tableData.reduce((acc, item) => {
      acc.push({
        id: item.userAgent,
        value: item.totalViews,
        label: item.label,
        color: item.color,
        textColor: item.textColor,
        visible: true
      })

      return acc
    }, [] as ChartDataItem[])
  }, [tableData])

  const csvData = useMemo(() => {
    if (!platformPopularityData?.length) return

    const data = platformPopularityData.map(
      ({
        userAgent,
        totalViews,
        totalBytes,
        totalDuration,
        averageDuration,
        percentageViews,
        uniqueViews
      }) => ({
        [STATISTICS_CSV_HEADERS.USER_AGENT]: userAgent,
        [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: totalViews,
        [STATISTICS_CSV_HEADERS.TOTAL_BYTES]: totalBytes,
        [STATISTICS_CSV_HEADERS.TOTAL_DURATON]: totalDuration,
        [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: averageDuration,
        [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: percentageViews,
        [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: uniqueViews
      })
    )

    return {
      csvHeaders: CSV_HEADERS,
      data
    }
  }, [platformPopularityData])

  if (!platformPopularityData) {
    return (
      <StatisticsCardSkeleton testId={PLATFORM_POPULARITY_SKELETON_TEST_ID} height={500}>
        <HorizontalTableSkeleton columns={7} rows={4} />
      </StatisticsCardSkeleton>
    )
  }

  return (
    <StatisticsCard title="Platforms" testId={PLATFORM_POPULARITY_CARD_TEST_ID} csvData={csvData}>
      <PieChart
        pieSeriesProps={{
          innerRadius: 100
        }}
        onHighlightChange={setHighlightedItem}
        chartData={chartData}
      />
      <HorizontalTable
        data={tableData}
        rows={PLATFORM_POPULARITY_ROWS}
        shouldHighlightColumn={(column) => column?.userAgent === highlightedItem?.id}
      />
    </StatisticsCard>
  )
}
